/**
 * RecordingUploadBanner — Nov 2026
 * Teacher-only slim strip at the top of the dashboard: shows pending class
 * recording uploads (progress / paused / waiting for retry) with a
 * «استئناف الآن» action. Renders nothing when there is nothing to upload.
 */
import { useCallback, useEffect, useState } from 'react';
import { CloudUpload, Loader2, WifiOff, CheckCircle2, RefreshCw, X } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { listPendingFiles } from '../lib/classRecorder';
import { drainPendingUploads, onUploadProgress } from '../lib/recordingUploader';

const fmtMB = (n) => `${Math.max(1, Math.round(n / 1048576))} MB`;

const RecordingUploadBanner = () => {
  const { user } = useAuth();
  const isTeacher = user?.role === 'Teacher';
  const [pending, setPending] = useState([]);
  const [status, setStatus] = useState({ phase: 'idle' });
  const [hidden, setHidden] = useState(false);
  const [, tick] = useState(0);

  const refresh = useCallback(async () => {
    if (!isTeacher) return;
    try { setPending(await listPendingFiles()); } catch { setPending([]); }
  }, [isTeacher]);

  useEffect(() => { refresh(); }, [refresh]);
  useEffect(() => onUploadProgress((p) => {
    setStatus(p);
    if (p.phase === 'done' || p.phase === 'idle') refresh();
  }), [refresh]);
  useEffect(() => {
    if (status.phase !== 'waiting') return undefined;
    const t = setInterval(() => tick((n) => n + 1), 1000);
    return () => clearInterval(t);
  }, [status.phase]);

  if (!isTeacher || hidden) return null;
  const done = status.phase === 'done' && pending.length === 0;
  if (!pending.length && !done) return null;
  const totalBytes = pending.reduce((a, f) => a + f.size, 0);
  const pct = status.total ? Math.round(((status.done || 0) / status.total) * 100) : 0;
  const uploading = status.phase === 'uploading';
  const waiting = status.phase === 'waiting';
  const secs = waiting ? Math.max(0, Math.ceil((status.retryAt - Date.now()) / 1000)) : 0;

  let Icon = CloudUpload;
  let title = `تسجيل ${pending.length > 1 ? `${pending.length} حصص` : 'حصة'} بانتظار الرفع`;
  let sub = `${fmtMB(totalBytes)} على هذا الجهاز — يُرفع تلقائياً أثناء بقاء المنصة مفتوحة`;
  if (uploading) {
    Icon = Loader2; title = 'جارٍ رفع تسجيل الحصة';
    sub = `${status.done || 0} من ${status.total || 0} جزءاً · لا تغلق المنصة حتى يكتمل`;
  } else if (waiting) {
    Icon = WifiOff; title = status.reason === 'offline' ? 'الرفع متوقف — لا يوجد إنترنت' : 'الرفع متوقف مؤقتاً';
    sub = `سيُعاد تلقائياً خلال ${secs} ث من حيث توقف`;
  } else if (done) {
    Icon = CheckCircle2; title = 'اكتمل رفع تسجيل الحصة'; sub = 'أصبح متاحاً للطلاب في صفحة التسجيلات';
  }

  return (
    <div dir="rtl" data-testid="recording-upload-banner" role="status"
      className="mb-4 rounded-xl overflow-hidden"
      style={{ background: '#0F172A', border: '1px solid rgba(245,194,66,0.28)' }}>
      <div className="flex items-center gap-3 px-3.5 py-2.5 sm:px-4">
        <span className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0"
          style={{ background: 'rgba(245,194,66,0.12)', color: done ? '#6EE7B7' : '#F5C242' }}>
          <Icon size={17} className={uploading ? 'animate-spin' : ''} />
        </span>
        <div className="min-w-0 flex-1">
          <p className="text-[12px] sm:text-[13px] font-black text-white truncate" data-testid="recording-upload-banner-title">{title}</p>
          <p className="text-[10px] sm:text-[11px] font-bold truncate" style={{ color: 'rgba(255,255,255,0.6)' }}
            data-testid="recording-upload-banner-sub">{sub}</p>
        </div>
        {!uploading && !done && (
          <button type="button" onClick={() => drainPendingUploads()} data-testid="recording-upload-resume-btn"
            className="inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-[11px] font-black transition-transform hover:scale-105 shrink-0"
            style={{ background: '#F5C242', color: '#0F172A' }}>
            <RefreshCw size={12} /> استئناف الآن
          </button>
        )}
        {uploading && (
          <span className="text-[12px] font-black tabular-nums shrink-0" style={{ color: '#F5C242' }} data-testid="recording-upload-banner-pct">{pct}%</span>
        )}
        {done && (
          <button type="button" onClick={() => setHidden(true)} data-testid="recording-upload-banner-close"
            className="text-white/50 hover:text-white shrink-0" aria-label="إغلاق"><X size={16} /></button>
        )}
      </div>
      <div className="h-1 w-full" style={{ background: 'rgba(255,255,255,0.08)' }}>
        <div className="h-full transition-[width] duration-500" data-testid="recording-upload-banner-bar"
          style={{ width: `${done ? 100 : pct}%`, background: done ? '#10B981' : '#F5C242' }} />
      </div>
    </div>
  );
};

export default RecordingUploadBanner;
